/*
 * Black-and-white hover reveal that spreads from the center out, but unevenly:
 * blocky pixels (to match the pixelated photoshoot), a ragged noisy edge, and some rows
 * that streak ahead of the rest like the photo's scan smears.
 *
 * Markup: any element with data-bw-reveal containing the color image and, on top of it,
 * a grayscale copy with class "bw". The script masks the grayscale copy block by block.
 *   <a data-bw-reveal> <img src="x.png"> <img class="bw" src="x.png"> </a>
 * Optional attributes on the data-bw-reveal element:
 *   data-bw-blocks  blocks across (default 56; lower = chunkier, higher = finer)
 *   data-bw-in      seconds to go black and white (default 1.6)
 *   data-bw-out     seconds to go back to color (default 1.0)
 */
(() => {
  const SCALE = 4;    // mask pixels per block (keeps block edges crisp when the mask is stretched)
  const BAND = 0.07;  // softness of the moving edge, in reveal-progress units

  function valueNoise(cols, rows) {
    const g = Array.from({ length: (rows + 1) * (cols + 1) }, () => Math.random());
    const at = (x, y) => g[y * (cols + 1) + x];
    return (u, v) => {
      const x = u * cols, y = v * rows, x0 = Math.floor(x), y0 = Math.floor(y);
      const fx = x - x0, fy = y - y0, sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy);
      const x1 = Math.min(x0 + 1, cols), y1 = Math.min(y0 + 1, rows);
      const a = at(x0, y0) + (at(x1, y0) - at(x0, y0)) * sx;
      const b = at(x0, y1) + (at(x1, y1) - at(x0, y1)) * sx;
      return a + (b - a) * sy;
    };
  }

  function setup(root) {
    const gray = root.querySelector(".bw");
    if (!gray) return;
    const W = +root.dataset.bwBlocks || 56;
    // grid follows the picture's shape (falls back to 3:4 if it is hidden while measuring)
    const ratio = gray.offsetWidth ? gray.offsetHeight / gray.offsetWidth : 4 / 3;
    const H = Math.max(1, Math.round(W * ratio));
    const tIn = (+root.dataset.bwIn || 1.6) * 1000, tOut = (+root.dataset.bwOut || 1.0) * 1000;

    // When each block turns: distance from center, bent by two layers of noise, plus rows
    // that run ahead (streaks) and a little per-block grit.
    const big = valueNoise(4, 5), fine = valueNoise(11, 14);
    const rowLead = Array.from({ length: H }, () => (Math.random() < 0.3 ? Math.random() * 0.28 : 0));
    const order = new Float32Array(W * H);
    let lo = Infinity, hi = -Infinity;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      const u = (x + 0.5) / W, v = (y + 0.5) / H;
      const d = Math.hypot((u - 0.5) * (W / H > 1 ? 1 : W / H), v - 0.5) * 1.6;
      const t = d + (big(u, v) - 0.5) * 0.55 + (fine(u, v) - 0.5) * 0.2
        - rowLead[y] * (1 - Math.abs(u - 0.5)) + Math.random() * 0.05;
      order[y * W + x] = t; lo = Math.min(lo, t); hi = Math.max(hi, t);
    }
    for (let i = 0; i < order.length; i++) order[i] = (order[i] - lo) / (hi - lo);

    const c = document.createElement("canvas");
    c.width = W * SCALE; c.height = H * SCALE;
    const ctx = c.getContext("2d");

    function paint(p) {
      ctx.clearRect(0, 0, c.width, c.height);
      const reach = p * (1 + BAND);
      for (let i = 0; i < order.length; i++) {
        const a = Math.min(Math.max((reach - order[i]) / BAND, 0), 1);
        if (a <= 0) continue;
        ctx.fillStyle = `rgba(0,0,0,${a})`;
        ctx.fillRect((i % W) * SCALE, Math.floor(i / W) * SCALE, SCALE, SCALE);
      }
      const url = `url(${c.toDataURL()})`;
      gray.style.webkitMaskImage = url;
      gray.style.maskImage = url;
    }

    gray.style.webkitMaskSize = gray.style.maskSize = "100% 100%";
    gray.style.webkitMaskRepeat = gray.style.maskRepeat = "no-repeat";
    paint(0);
    root.classList.add("bw-ready");

    const calm = matchMedia("(prefers-reduced-motion: reduce)").matches;
    let p = 0, target = 0, last = 0, raf = 0;
    function tick(now) {
      const dt = last ? now - last : 16; last = now;
      const step = dt / (target > p ? tIn : tOut);
      p = target > p ? Math.min(p + step, target) : Math.max(p - step, target);
      paint(p);
      raf = p === target ? 0 : requestAnimationFrame(tick);
    }
    function go(to) {
      target = to;
      if (calm) { p = to; paint(p); return; }
      if (!raf) { last = 0; raf = requestAnimationFrame(tick); }
    }
    root.addEventListener("pointerenter", (e) => { if (e.pointerType !== "touch") go(1); });
    root.addEventListener("pointerleave", (e) => { if (e.pointerType !== "touch") go(0); });
    root.addEventListener("focus", () => go(1));
    root.addEventListener("blur", () => go(0));
    root.addEventListener("pointerdown", (e) => { if (e.pointerType === "touch") go(target ? 0 : 1); });
  }

  function init() {
    document.querySelectorAll("[data-bw-reveal]").forEach((el) => {
      const img = el.querySelector("img");
      if (img && !img.complete) img.addEventListener("load", () => setup(el), { once: true });
      else setup(el);
    });
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
